import React, { Component } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { parse as qsParse, stringify as qsStringify } from 'query-string';
import { debounce, isEmpty as empty } from 'lodash';
import { TableSortIcon, ButtonPrimary } from '@spotify-internal/encore-creator-web';
import { scroller as scroll } from 'react-scroll';

import { ConnectedFilterWithDatePicker } from '../../Shared/FilterWithDatePicker';
import { Pagination } from '../../Shared/Pagination';
import { LoadingIndicator } from '../../Shared/LoadingIndicator';

import { PodcastPending } from '../PodcastPending';
import styles from './index.module.scss';
import {
  LOADING,
  ERROR,
  selectDateRange,
  selectUserShows,
  selectUserShowsStatus,
  selectPendingPodcasts,
  selectPendingPodcastsStatus,
  selectNetworkMetadata,
  selectCurrentNetworkId,
} from '../../../store/selectors';
import { loadUserShows, loadPendingPodcasts } from '../../../store/meShows';
import { downloadPodcastListAsCsv } from '../../../store/downloadCsv';
import { formatDate, commaFormatNumber } from '../../../lib/Formatter/index';
import { trackEvent } from '../../../lib/Tracking/index';
import { Helmet } from 'react-helmet';
import { MeContext } from '../../../context/me';
import { screenMdMin, black } from '@spotify-internal/encore-foundation';
import { MeShows, PaginatedShows, ReduxState } from '../../../types';
import { TableHeaderLabel, TableHeaderCell, TableRowLink } from '../../Shared/Table';

import { Banner, Table, TableRow, TableCell, TableThumbnail } from '@spotify-internal/encore-web';

import styled, { ThemeProvider } from 'styled-components';

type Props = {
  networkPodcastList?: {
    shows: MeShows[];
  } & PaginatedShows;
  status?: typeof LOADING | typeof ERROR;
  pendingPodcastsStatus?: typeof LOADING | typeof ERROR;
  loadUserShows: typeof loadUserShows;
  downloadPodcastListAsCsv: typeof downloadPodcastListAsCsv;
  dateRange?: {
    startDate: string;
    endDate: string;
    label: string;
  };
  pendingPodcasts?: {
    artworkUrl?: string;
    name?: string;
    author?: string;
    status?: string;
  }[];
  loadPendingPodcasts: typeof loadPendingPodcasts;
  networkMetadata?: {
    name?: string;
  };
  currentNetworkId?: string;
} & RouteComponentProps;

type State = {
  sortBy: string;
  sortDescending: boolean;
  isMobile: boolean;
};

const MediaQueryList = window.matchMedia(`(min-width: ${screenMdMin})`);

export class PodcastList extends Component<Props, State> {
  state = {
    sortBy: 'name',
    sortDescending: false,
    isMobile: !MediaQueryList.matches,
  };

  static contextType = MeContext;

  matchMediaListener =
    /* istanbul ignore next */
    event => {
      /* istanbul ignore next */
      this.setState({ isMobile: !event.matches });
    };

  isTryingToLoadEntireCatalog = () => {
    // If there isn't a network ID set AND the user has god mode, then S4P will try and load the entire Spotify catalog and time out
    return !this.props.currentNetworkId && this.context.permissions.godMode;
  };

  componentDidMount() {
    if (!this.isTryingToLoadEntireCatalog()) {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
      this.props.loadPendingPodcasts();
    }

    MediaQueryList.addListener(this.matchMediaListener);
  }

  componentWillUnmount() {
    MediaQueryList.removeListener(this.matchMediaListener);
  }

  /* istanbul ignore next */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!this.props.dateRange) return;
    if (
      nextProps.dateRange.startDate !== this.props.dateRange.startDate ||
      nextProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    }
  }

  /* istanbul ignore next */
  async componentDidUpdate() {
    if (
      this.props.networkPodcastList &&
      this.props.networkPodcastList.currentPage !== this.currentPage
    ) {
      await this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
      scroll.scrollTo('filter-with-date-picker');
    }
  }

  get queryParams() {
    return qsParse(this.props.location.search);
  }

  get searchQuery() {
    if (typeof this.queryParams.search === 'string') {
      return this.queryParams.search;
    }

    return '';
  }

  get currentPage() {
    if (typeof this.queryParams.page === 'string') {
      return parseInt(this.queryParams.page, 10);
    }

    return 1;
  }

  filterPodcastList = debounce(
    /* istanbul ignore next */
    () => {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    },
    500,
  );

  formatQueryParams = params => qsStringify({ ...this.queryParams, ...params });

  handleTableRowClick = podcastId => () => this.props.history.push(`/podcast/${podcastId}`);

  formatValueByDataAvailability = (key, podcast) => {
    if (podcast[key] === null) return '-';
    return commaFormatNumber(podcast[key]);
  };

  handlePageChange() {
    return ({ selected }) => {
      const { history, location } = this.props;
      const queryParams = this.formatQueryParams({ page: selected });

      history.push(`${location.pathname}?${queryParams}${location.hash || ''}`);
      trackEvent({ category: 'podcast-list', action: 'page-change', label: selected });
      scroll.scrollTo('filter-with-date-picker');

      return this.props.loadUserShows(
        this.searchQuery,
        selected,
        this.state.sortBy,
        this.state.sortDescending,
      );
    };
  }

  handleSearchInput() {
    return query => {
      trackEvent({ category: 'podcast', action: 'search', label: query });

      const { history, location } = this.props;
      const isEmptyQuery = empty(query.trim());
      /* istanbul ignore next */
      const queryParams = this.formatQueryParams({
        search: isEmptyQuery ? undefined : query,
        page: 1,
      });
      history.replace(`${location.pathname}?${queryParams}${location.hash || ''}`);
      this.filterPodcastList();
    };
  }

  handleColumnSort = columnKey => () => {
    let sortDescending = true;
    /* istanbul ignore else */
    if (columnKey === this.state.sortBy) {
      sortDescending = !this.state.sortDescending;
    }
    trackEvent({ category: 'podcast-list', action: 'sort', label: columnKey });
    this.setState({ sortBy: columnKey, sortDescending }, () => {
      this.props.loadUserShows(
        this.searchQuery,
        this.currentPage,
        this.state.sortBy,
        this.state.sortDescending,
      );
    });
  };

  handleCsvDownload = /* istanbul ignore next */ () => {
    this.props.downloadPodcastListAsCsv(
      this.searchQuery,
      this.state.sortBy,
      this.state.sortDescending,
    );
  };

  renderColumnSortIcon = (columnKey: string) => {
    if (columnKey === this.state.sortBy) {
      return <TableSortIcon direction={this.state.sortDescending ? 'down' : 'up'} />;
    }
    return null;
  };

  renderTableHead = () => {
    const isDesktop = !this.state.isMobile;

    return (
      <TableRow>
        <TableHeaderCell onClick={this.handleColumnSort('name')}>
          <TableHeaderLabel
            style={{ justifyContent: 'flex-start' }}
            active={this.state.sortBy === 'name'}
            label={`${this.props.networkPodcastList!.totalShows} ${
              /* istanbul ignore next */ this.props.networkPodcastList!.totalShows === 1
                ? 'podcast'
                : 'podcasts'
            }`}
            childrenOrderedFirst={false}
          >
            {this.renderColumnSortIcon('name')}
          </TableHeaderLabel>
        </TableHeaderCell>
        <TableHeaderCell onClick={this.handleColumnSort('starts')}>
          <TableHeaderLabel active={this.state.sortBy === 'starts'} label="Starts">
            {this.renderColumnSortIcon('starts')}
          </TableHeaderLabel>
        </TableHeaderCell>
        {isDesktop && (
          <>
            <TableHeaderCell onClick={this.handleColumnSort('streams')}>
              <TableHeaderLabel active={this.state.sortBy === 'streams'} label="Streams">
                {this.renderColumnSortIcon('streams')}
              </TableHeaderLabel>
            </TableHeaderCell>
            <TableHeaderCell onClick={this.handleColumnSort('listeners')}>
              <TableHeaderLabel active={this.state.sortBy === 'listeners'} label="Listeners">
                {this.renderColumnSortIcon('listeners')}
              </TableHeaderLabel>
            </TableHeaderCell>
            <TableHeaderCell onClick={this.handleColumnSort('releaseDate')}>
              <TableHeaderLabel
                active={this.state.sortBy === 'releaseDate'}
                label="Last Episode Date"
              >
                {this.renderColumnSortIcon('releaseDate')}
              </TableHeaderLabel>
            </TableHeaderCell>
          </>
        )}
      </TableRow>
    );
  };

  renderTableBody(podcasts) {
    const isDesktop = !this.state.isMobile;

    return podcasts.map(podcast => (
      <TableRow
        key={podcast.id}
        hover
        onClick={this.handleTableRowClick(podcast.id)}
        data-testid="podcast-list-table-row"
        style={{ cursor: 'pointer' }}
      >
        <TableCell className={classNames(styles['artwork-cell'], styles.bordered)}>
          <TableThumbnail
            img={podcast.artworkUrl}
            imgAlt="Podcast Artwork"
            thumbnailTitle={
              <TableRowLink to={`/podcast/${podcast.id}`}>{podcast.name}</TableRowLink>
            }
            small
          />
        </TableCell>
        <TableCell align="right" numerical>
          {this.formatValueByDataAvailability('starts', podcast)}
        </TableCell>
        {isDesktop && (
          <>
            <TableCell align="right" numerical>
              {this.formatValueByDataAvailability('streams', podcast)}
            </TableCell>
            <TableCell align="right" numerical>
              {this.formatValueByDataAvailability('listeners', podcast)}
            </TableCell>
            <TableCell align="right" numerical>
              {formatDate(podcast.releaseDate, 'D MMM YYYY')}
            </TableCell>
          </>
        )}
      </TableRow>
    ));
  }

  renderColGroup = () => {
    const isDesktop = !this.state.isMobile;

    /* istanbul ignore else */
    if (isDesktop) {
      return (
        <colgroup>
          <col />
          <col width="100" />
          <col width="100" />
          <col width="100" />
          <col width="160" />
        </colgroup>
      );
    }

    /* istanbul ignore next */
    return (
      <colgroup>
        <col />
        <col width="100" />
      </colgroup>
    );
  };

  renderLoader = () => (
    <div className={styles.loader}>
      <LoadingIndicator style={{ height: '35vh', overflow: 'hidden' }} />
    </div>
  );

  render() {
    const podcastList = this.props.networkPodcastList;

    if (this.props.status === ERROR) {
      return <span data-qa="network-podcast-list-error" />;
    }

    if (this.isTryingToLoadEntireCatalog()) {
      return (
        <Banner data-testid="god-mode-catalog-banner" variant={Banner.announcement}>
          Select a <a href="/networks">Network</a> to view the catalog
        </Banner>
      );
    }

    if (!podcastList) {
      if (this.props.status === LOADING) {
        <div data-qa="network-podcast-list-loading" className={styles.container}>
          {this.renderLoader()}
        </div>;
      }

      return null;
    } else {
      return (
        <div data-qa="network-podcast-list" className={styles.container}>
          <Helmet
            title={`Catalog ${
              /* istanbul ignore next */ this.props.networkMetadata
                ? ` - ${this.props.networkMetadata.name}`
                : ''
            }`}
          />
          {this.props.pendingPodcasts && this.props.pendingPodcasts.length > 0 && (
            <PodcastPending
              pendingPodcasts={this.props.pendingPodcasts}
              pendingPodcastsStatus={this.props.pendingPodcastsStatus}
            />
          )}
          {/* istanbul ignore next */ this.props.status === LOADING && this.renderLoader()}
          <ConnectedFilterWithDatePicker
            downloadDataAsCsv={this.handleCsvDownload}
            onUserInput={this.handleSearchInput()}
            csvDownloadTrackingLabel="podcast-list"
            value={this.searchQuery}
          />
          <Table>
            {this.renderColGroup()}
            <thead>{this.renderTableHead()}</thead>
            <tbody>{this.renderTableBody(podcastList.shows)}</tbody>
          </Table>
          <Pagination
            onPageChangeHandler={this.handlePageChange()}
            pageCount={podcastList.totalPages || 0}
          />
        </div>
      );
    }
  }
}

const LinkButton = styled(ButtonPrimary).attrs({
  forwardedAs: 'a',
  buttonSize: ButtonPrimary.sm,
})`
  border-radius: 4px;
  padding: 3px 7px;
  letter-spacing: normal;
  text-transform: none;
  font-weight: bold;
  line-height: 17px;
`;

const selector = createStructuredSelector<
  ReduxState,
  {
    networkPodcastList: ReturnType<typeof selectUserShows>;
    status: ReturnType<typeof selectUserShowsStatus>;
    pendingPodcastsStatus: ReturnType<typeof selectPendingPodcastsStatus>;
    dateRange: ReturnType<typeof selectDateRange>;
    pendingPodcasts: ReturnType<typeof selectPendingPodcasts>;
    networkMetadata: ReturnType<typeof selectNetworkMetadata>;
    currentNetworkId: ReturnType<typeof selectCurrentNetworkId>;
  }
>({
  networkPodcastList: selectUserShows,
  status: selectUserShowsStatus,
  pendingPodcastsStatus: selectPendingPodcastsStatus,
  dateRange: selectDateRange,
  pendingPodcasts: selectPendingPodcasts,
  networkMetadata: selectNetworkMetadata,
  currentNetworkId: selectCurrentNetworkId,
});

const actions = { loadUserShows, downloadPodcastListAsCsv, loadPendingPodcasts };
export const ConnectedPodcastList = withRouter(connect(selector, actions)(PodcastList));
